/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { useNavigate } from '@tanstack/react-router';
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAccount } from '~/apis';
import {
  env,
  FullscreenContext,
  gameLogger,
  selectConnected,
  selectCurrentState,
  selectDebugSpawnSource,
  selectIsHistoryMode,
  selectIsNewGame,
  selectRestoreState,
  selectUserId,
  useTranslation,
} from '~/app';
import { clearLocalGameSnapshot } from '~/app/controllerLocalWrapper';
import { ROUTE_INDEX, ROUTE_RESULTS } from '~/app/routes';
import { resolveTrainingStorageScopeId, setCurrentStorageUserScopeId } from '~/app/storageScope';
import { useAuthorizedDispatchOnMount } from '~/app/useAuthReady';
import Board from '~/board';
import FullscreenDialog from '~/components/FullscreenDialog';
import Overlay from '~/overlay';
import Turfmaster from '~/server/src/tmmodel';
import { restoreGame } from '~/setup';
import { clearPersistedDebugSpawnSource } from '~/setup/debugSpawnStorage';
import { getPreferredReconnectGameId } from '~/setup/reconnectStorage';
import { actions as setupActions } from '~/setup/state';
import { requiresPremiumForOnlineMode } from '~/utils/gameAccessGate';
import { useIsMobile } from '~/utils/useClientOnly';

import { GameMode } from '.';

const Game = () => {
  const dispatch = useDispatch();
  const { urlPath } = useTranslation();
  const { data: account, isLoading: isAccountLoading } = useAccount();
  const navigate = useNavigate();
  const currentState = useSelector(selectCurrentState);
  const connected = useSelector(selectConnected);
  const connectionUserId = useSelector(selectUserId);
  const isNewGame = useSelector(selectIsNewGame);
  const restoreState = useSelector(selectRestoreState);
  const debugSpawnSource = useSelector(selectDebugSpawnSource);

  const { isFullscreen, setIsFullscreen, dontAskAgain, setDontAskAgain } =
    useContext(FullscreenContext);
  const [isFullscreenDialogOpen, setIsFullscreenDialogOpen] = useState(
    !isFullscreen && !dontAskAgain,
  );
  const isMobile = useIsMobile();

  const mode = useMemo<GameMode>(() => {
    switch (urlPath) {
      case '/training':
        return 'training';
      case '/freegame':
        return 'free';
      case '/tournament':
        return 'tournament';
      default:
        return 'training';
    }
  }, [urlPath]);

  const trainingStorageScopeId = useMemo(
    () =>
      resolveTrainingStorageScopeId({
        accountUserId: account?.userId,
        connectionUserId,
      }),
    [account?.userId, connectionUserId],
  );

  // Reset restore state when component mounts to allow subsequent reloads
  useEffect(() => {
    if (!isNewGame && restoreState !== 'none') {
      gameLogger.debug({ restoreState }, 'Game.tsx: Resetting restore state to allow reloads');
      dispatch(setupActions.setRestoreState('none'));
    }
  }, [dispatch, isNewGame, restoreState]);

  // For training mode: if logged in, wait for account; if logged out, dispatch immediately
  // For online modes: always wait for auth and account
  useAuthorizedDispatchOnMount(
    useCallback(
      (dispatch: ReturnType<typeof useDispatch>) => {
        if (!isNewGame && restoreState === 'none') {
          const restoreUserId = mode === 'training' ? trainingStorageScopeId : account?.userId;
          const preferredGameId = getPreferredReconnectGameId(restoreUserId);
          gameLogger.debug(
            {
              mode,
              accountUserId: account?.userId,
              isAccountLoading,
              hasAccount: !!account,
              connected,
            },
            'Game.tsx: Dispatching restoreGame',
          );

          dispatch(
            restoreGame({
              mode,
              userId: restoreUserId,
              preferredGameId,
            }),
          );
        }
      },
      [account, isAccountLoading, connected, isNewGame, restoreState, mode, trainingStorageScopeId],
    ),
  );

  // Check subscription for online modes (free + tournament)
  useEffect(() => {
    if (!account || isAccountLoading) return;
    if (mode !== 'free' && mode !== 'tournament') return;

    if (requiresPremiumForOnlineMode(mode, account, debugSpawnSource, account.userId)) {
      gameLogger.warn(
        { mode, debugSpawnSource },
        'online mode blocked: no valid subscription, redirect to home',
      );
      navigate({
        to: ROUTE_INDEX,
        replace: true,
        search: {
          blocked:
            mode === 'tournament'
              ? 'tournament_subscription_required'
              : 'free_subscription_required',
        },
      });
    }
  }, [account, debugSpawnSource, isAccountLoading, mode, navigate]);

  const isHistoryMode = useSelector(selectIsHistoryMode);

  useEffect(() => {
    const nextScopeId =
      mode === 'training'
        ? trainingStorageScopeId
        : account?.userId && account.userId.length > 0
          ? account.userId
          : undefined;
    setCurrentStorageUserScopeId(nextScopeId);
  }, [account?.userId, mode, trainingStorageScopeId]);

  useEffect(() => {
    if (currentState === Turfmaster.stateMachine.ENDGAME && !isHistoryMode) {
      // remove persisted training state when game is finished
      if (mode === 'training') clearLocalGameSnapshot(trainingStorageScopeId);
      if (mode === 'free' || mode === 'tournament') {
        clearPersistedDebugSpawnSource(account?.userId);
      }
      navigate({ to: ROUTE_RESULTS, replace: true });
    }
  }, [account?.userId, currentState, navigate, mode, isHistoryMode, trainingStorageScopeId]);

  useEffect(() => {
    if (currentState === Turfmaster.stateMachine.LEAVEGAME) {
      // remove persisted training state when player leaves game
      if (mode === 'training') clearLocalGameSnapshot(trainingStorageScopeId);
      if (mode === 'free' || mode === 'tournament') {
        clearPersistedDebugSpawnSource(account?.userId);
      }
      // navigate to home when player leaves game
      navigate({ to: ROUTE_INDEX, replace: true });
    }
  }, [account?.userId, currentState, navigate, mode, trainingStorageScopeId]);

  useEffect(() => {
    if (connected) gameLogger.debug({ currentState }, 'CONNECTED');
    else {
      if (env.dev) gameLogger.debug({ currentState }, 'NOT CONNECTED');
    }
  }, [connected, currentState, dispatch, isNewGame, mode]);

  useEffect(() => {
    // if restore was unsuccessful navigate to home
    gameLogger.debug(
      {
        isNewGame,
        restoreState,
        condition: !isNewGame && restoreState === 'error',
      },
      'Game.tsx: Checking restore error condition',
    );
    if (!isNewGame && restoreState === 'error') {
      gameLogger.warn('Game.tsx: Restore error, redirecting to home');
      navigate({ to: ROUTE_INDEX, replace: true });
    }
  }, [isNewGame, navigate, restoreState]);

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <Board />

      <Overlay
        onCloseHandler={() => {
          // remove persisted training state when user exits game
          if (mode === 'training') clearLocalGameSnapshot(trainingStorageScopeId);
          if (mode === 'free' || mode === 'tournament') {
            clearPersistedDebugSpawnSource(account?.userId);
          }
          navigate({ to: ROUTE_INDEX, replace: true });
        }}
      />

      {!isFullscreen && !dontAskAgain && isMobile && (
        <FullscreenDialog
          open={isFullscreenDialogOpen}
          onClose={(dontAskAgain) => {
            setIsFullscreenDialogOpen(false);
            if (dontAskAgain) setDontAskAgain(true);
          }}
          onAccept={(dontAskAgain) => {
            setIsFullscreenDialogOpen(false);
            setIsFullscreen(true);
            if (dontAskAgain) setDontAskAgain(true);
          }}
        />
      )}
    </div>
  );
};

export default memo(Game);
